import React, { useState, useEffect } from 'react'
import '../../App.css'
import Axios from 'axios'
import ClipLoader from 'react-spinners/ClipLoader'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { formatZARCurrency } from '../../MoneyFormat'
import { API_URL } from '../../config'

const COLS = 8

const toTitleCase = (str = '') =>
  String(str)
    .toLowerCase()
    .replace(/(^|[\s\-–(/&])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase())

const num = (v) => parseFloat(v) || 0
const money = (v) => formatZARCurrency(String(num(v)))

const formatDate = (value) => {
  if (!value) return '—'
  const d = new Date(String(value).replace(' ', 'T'))
  if (isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const GREEN = { color: '#1b7f4b', bg: '#e3f6ec' }
const AMBER = { color: '#a86a00', bg: '#fff3d6' }
const RED = { color: '#b3261e', bg: '#fde8e6' }

const STATUS_MAP = {
  Verification: { ...AMBER, label: 'Verifying', note: 'Funds are reserved while Ozow verifies.' },
  SubmittedForProcessing: { ...AMBER, label: 'In Transit', note: 'Funds are in transit.' },
  PayoutPendingInvestigation: {
    ...AMBER,
    label: 'Under Investigation',
    note: 'Funds are locked until the investigation resolves.',
  },
  PayoutComplete: { ...GREEN, label: 'Paid', note: 'Funds have been paid out.' },
  PayoutReceived: { ...GREEN, label: 'Paid', note: 'Funds have been paid out.' },
  PayoutCancelled: {
    ...RED,
    label: 'Cancelled',
    note: 'Request failed, funds returned to your balance.',
  },
  VerificationFailed: {
    ...RED,
    label: 'Rejected',
    note: 'Request rejected, funds returned to your balance.',
  },
  PayoutProcessingError: {
    ...RED,
    label: 'Error',
    note: 'Processing error, funds returned to your balance.',
  },
}

const getStatus = (raw) =>
  STATUS_MAP[raw] || { ...RED, label: raw || 'Returned', note: 'Funds returned to your balance.' }

const PayoutRequestList = ({ eventid, count, setRefreshing, source, eventName }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [requests, setRequests] = useState([])
  const [clipLoading, setClipLoading] = useState(true)
  const baseURL = `${API_URL}/apps/kscanner/webservices.php`

  useEffect(() => {
    Axios.get(`${baseURL}?query=PAYOUT-REQUEST-LIST&event_id=${eventid}`, {
      withCredentials: true,
    })
      .then((response) => {
        if (setRefreshing) setRefreshing('')
        setClipLoading(false)
        const res = response.data
        setRequests(Array.isArray(res) ? res : [])
      })
      .catch((error) => {
        console.log(error)
        if (setRefreshing) setRefreshing('')
        setClipLoading(false)
      })
  }, [count])

  const totalRequested = requests.reduce((sum, r) => sum + num(r.amount_requested), 0)
  const totalPaid = requests.reduce((sum, r) => sum + num(r.amount_paid), 0)
  const totalPayFees = requests.reduce((sum, r) => sum + num(r.payout_fee), 0)

  const th = {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#6c757d',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: '#f6f8fa',
  }

  return (
    <div className="accordion-item">
      {/* Event name */}
      {eventName && (
        <div
          style={{
            margin: '0 0 16px',
            padding: '12px 16px',
            background: '#f6f8fa',
            borderLeft: '4px solid #17a2b8',
            borderRadius: 6,
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Event
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>
            {toTitleCase(eventName)}
          </div>
        </div>
      )}

      {/* Collapsible header */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          border: '1px solid #e3e7ec',
          borderRadius: isOpen ? '8px 8px 0 0' : 8,
          background: '#fff',
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            Payout Activity
            {!clipLoading && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  background: '#e8f4f8',
                  color: '#0f6f86',
                  borderRadius: 10,
                  padding: '2px 8px',
                }}
              >
                {requests.length}
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, color: '#6c757d' }}>View all your payout activities.</div>
        </div>
        <span style={{ color: '#6c757d' }}>{isOpen ? <FaChevronUp /> : <FaChevronDown />}</span>
      </div>

      {isOpen && (
        <div
          className="table-responsive"
          style={{
            border: '1px solid #e3e7ec',
            borderTop: 0,
            borderRadius: '0 0 8px 8px',
            background: '#fff',
          }}
        >
          <table className="table mb-0" style={{ fontSize: 14 }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Payout Type</th>
                <th style={th}>Amount Requested</th>
                <th style={th}>Amount Paid</th>
                <th style={th}>Payout Fee</th>
                <th style={th}>Status</th>
                <th style={th}>Reference</th>
                <th style={th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {clipLoading ? (
                <tr>
                  <td colSpan={COLS} className="text-center py-4">
                    <ClipLoader size={25} color="grey" />
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={COLS} className="text-center py-4" style={{ color: '#6c757d' }}>
                    No payout requests yet.
                  </td>
                </tr>
              ) : (
                <>
                  {requests.map((req) => {
                    const s = getStatus(req.payout_status)
                    return (
                      <tr key={req.payout_id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(req.created_at)}</td>
                        <td style={{ textTransform: 'capitalize' }}>{req.payout_type}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{money(req.amount_requested)}</td>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {money(req.amount_paid)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', color: '#b3261e' }}>
                          {money(req.payout_fee)}
                        </td>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '3px 10px',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              color: s.color,
                              background: s.bg,
                            }}
                          >
                            {s.label}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: '#6c757d', wordBreak: 'break-all' }}>
                          {req.audit_token}
                        </td>
                        <td style={{ fontSize: 13, color: '#495057', minWidth: 200 }}>{s.note}</td>
                      </tr>
                    )
                  })}
                </>
              )}
            </tbody>
            {!clipLoading && requests.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f6f8fa', fontWeight: 700 }}>
                  <td colSpan={2}>Totals</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{money(totalRequested)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{money(totalPaid)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{money(totalPayFees)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  )
}

export default PayoutRequestList
